"use client";

import { useEffect, useMemo, useState } from "react";
import Search from "../front/search";
import useApi, { Methods } from "@/src/global/hooks/useApi";
import Product from "../front/product";
import Loading from "../common/loading";

type HomeProps = {
  categoriesProducts: Array<{
    id: number;
    label: string;
    labelEn: string;
  }>;
  lang: "fr" | "en";
};

const Home = ({ categoriesProducts, lang }: HomeProps) => {
  const isFr = lang === "fr";
  const {
    response: responseProducts,
    loading: loadingProducts,
    callApi: callApiProducts,
  } = useApi();

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState<{ query: string; category: string }>({
    query: "",
    category: "",
  });

  // Mettre à jour la liste de produits
  useEffect(() => {
    if (responseProducts?.res) setProducts(responseProducts.res);
  }, [responseProducts]);

  // Gestion de la recherche
  const handleChangeCategory = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const category = e.target.value;
    setSearch((prev) => ({ ...prev, category }));
    callApiProducts(
      Methods.GET,
      `/api/products?category=${category}&query=${search.query}`
    );
  };

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearch((prev) => ({ ...prev, query }));
    callApiProducts(
      Methods.GET,
      `/api/products?category=${search.category}&query=${query}`
    );
  };

  // Rendu des produits
  const renderProducts = useMemo(() => {
    if (loadingProducts) {
      return (
        <div
          className="l-home__loading"
          title={isFr ? "Chargement des produits..." : "Loading products..."}
        >
          <Loading />
        </div>
      );
    }

    if (
      products.length === 0 &&
      (search.query !== "" || search.category !== "")
    ) {
      return (
        <div className="l-home__products__list">
          <p className="m-alert m-alert--center">
            {isFr
              ? "Aucun produit n'a été trouvé. Veuillez vérifier vos critères de recherche"
              : "No products were found. Please check your search criteria"}
          </p>
        </div>
      );
    }

    if (products.length > 0) {
      return (
        <div className="l-home__products__list">
          {products.map((x) => (
            <Product lang={lang} status={false} key={x.id} product={x} />
          ))}
        </div>
      );
    }

    return null;
  }, [products, loadingProducts, search, lang]);

  return (
    <div className="l-home">
      <div className="l-home__banner">
        <div className="container">
          <div className="l-home__banner__info">
            <h1 className="l-home__banner__title">
              {isFr
                ? "Notices et guides d’utilisation"
                : "Instructions and user guides"}
            </h1>
            <p className="l-home__banner__text">
              {isFr ? (
                <>
                  Bienvenue sur le centre de téléchargement de Surpass.
                  <br />
                  Accédez à toute la documentation concernant vos produits
                  Surpass.
                  <br />
                  <span>
                    Commencez en tapant votre recherche dans les champs
                    ci-dessous.
                  </span>
                </>
              ) : (
                <>
                  Welcome to the Surpass download center!
                  <br />
                  Access all documentation for your Surpass products.
                  <br />
                  <span>Start by typing your search in the fields below.</span>
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="l-home__products">
        <div className="container">
          <div className="l-home__products__wrapper">
            <div className="l-home__products__search">
              <Search
                libeleLabel={
                  isFr
                    ? "Recherche par libellé produit ou référence"
                    : "Search by product name or reference"
                }
                catLabel={isFr ? "Catégorie de produit" : "Product category"}
                categories={categoriesProducts}
                lang={lang}
                onChangeCategory={handleChangeCategory}
                onChangeSearch={handleChangeSearch}
              />
            </div>
            {renderProducts}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
